import { useState, useEffect } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/Button';
import type { User } from '../../data/types';
import { getUsers } from '../../services/users';
import { assignIncident } from '../../services/incidents';
import { useUser } from '../../context';

interface AssignmentPickerProps {
  tenantId: string;
  incidentId: string;
  assignedToUserId: string | null | undefined;
  assignedToName: string | null | undefined;
  onAssign?: (userId: string | null, userName: string | null) => void;
  variant?: 'button' | 'inline';
  className?: string;
}

export function AssignmentPicker({
  tenantId,
  incidentId,
  assignedToUserId,
  assignedToName,
  onAssign,
  variant = 'button',
  className,
}: AssignmentPickerProps) {
  const { currentUser } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const isAssigned = !!assignedToUserId;
  const isAssignedToMe = currentUser && assignedToUserId === currentUser.userId;

  // Fetch users when dropdown opens
  useEffect(() => {
    if (isOpen && users.length === 0) {
      setLoadingUsers(true);
      getUsers().then(result => {
        if (result.success) {
          setUsers(result.data);
        }
        setLoadingUsers(false);
      });
    }
  }, [isOpen, users.length]);

  const handleAssign = async (user: User | null) => {
    setAssigning(true);
    const response = await assignIncident(
      tenantId,
      incidentId,
      user?.userId || null,
      user?.displayName || null
    );

    if (response.success) {
      onAssign?.(user?.userId || null, user?.displayName || null);
    }

    setAssigning(false);
    setIsOpen(false);
  };

  const handleAssignToMe = async () => {
    if (!currentUser) return;
    await handleAssign(currentUser);
  };

  if (variant === 'inline') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        {isAssigned ? (
          <>
            <UserAvatar name={assignedToName || ''} size="sm" />
            <span className="text-sm font-medium text-text-primary">
              {assignedToName}
            </span>
            {!isAssignedToMe && (
              <Button size="sm" variant="ghost" onClick={handleAssignToMe} disabled={assigning || !currentUser}>
                Reassign to me
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => handleAssign(null)} disabled={assigning}>
              Unassign
            </Button>
          </>
        ) : (
          <>
            <span className="text-sm text-text-secondary">Unassigned</span>
            <Button size="sm" onClick={handleAssignToMe} disabled={assigning || !currentUser}>
              {assigning ? 'Assigning...' : 'Assign to me'}
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors',
          isAssigned
            ? 'bg-bg-tertiary hover:bg-bg-tertiary'
            : 'bg-bg-tertiary hover:bg-bg-tertiary border border-dashed border-border-default'
        )}
        disabled={assigning}
      >
        {isAssigned ? (
          <>
            <UserAvatar name={assignedToName || ''} size="sm" />
            <span className="text-sm font-medium text-text-primary">
              {assignedToName}
            </span>
          </>
        ) : (
          <>
            <UnassignedIcon className="w-5 h-5 text-text-muted" />
            <span className="text-sm text-text-secondary">Unassigned</span>
          </>
        )}
        <ChevronDownIcon className={cn('w-4 h-4 text-text-muted transition-transform', isOpen && 'rotate-180')} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute top-full right-0 mt-1 z-50 w-64 bg-bg-secondary rounded-lg shadow-lg border border-border-default py-1 animate-fade-in">
            {/* Quick assign to me */}
            {currentUser && !isAssignedToMe && (
              <button
                onClick={handleAssignToMe}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-bg-tertiary/50 transition-colors border-b border-border-default"
              >
                <UserAvatar name={currentUser.displayName} size="sm" />
                <div>
                  <div className="text-sm font-medium text-text-primary">
                    Assign to me
                  </div>
                  <div className="text-xs text-text-secondary">
                    {currentUser.displayName}
                  </div>
                </div>
              </button>
            )}

            {/* User list */}
            <div className="max-h-64 overflow-y-auto py-1">
              <div className="px-3 py-1.5 text-xs font-medium text-text-secondary uppercase">
                Team Members
              </div>
              {loadingUsers ? (
                <div className="px-3 py-4 text-center text-sm text-text-secondary">
                  Loading...
                </div>
              ) : (
                users
                  .filter(u => u.isActive)
                  .map(user => (
                    <button
                      key={user.userId}
                      onClick={() => handleAssign(user)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
                        user.userId === assignedToUserId
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : 'hover:bg-bg-tertiary/50'
                      )}
                    >
                      <UserAvatar name={user.displayName} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-primary truncate">
                          {user.displayName}
                        </div>
                        <div className="text-xs text-text-secondary truncate">
                          {user.email}
                        </div>
                      </div>
                      {user.userId === assignedToUserId && (
                        <CheckIcon className="w-4 h-4 text-accent-blue" />
                      )}
                    </button>
                  ))
              )}
            </div>

            {/* Unassign option */}
            {isAssigned && (
              <button
                onClick={() => handleAssign(null)}
                className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-bg-tertiary/50 transition-colors border-t border-border-default text-text-secondary"
              >
                <UnassignedIcon className="w-5 h-5" />
                <span className="text-sm">Unassign</span>
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// SIMPLE ASSIGNMENT BADGE
// ============================================================================

interface AssignmentBadgeProps {
  assignedToName: string | null | undefined;
  size?: 'sm' | 'md';
  className?: string;
}

export function AssignmentBadge({ assignedToName, size = 'md', className }: AssignmentBadgeProps) {
  if (!assignedToName) {
    return (
      <span className={cn(
        'text-text-secondary',
        size === 'sm' ? 'text-xs' : 'text-sm',
        className
      )}>
        Unassigned
      </span>
    );
  }

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <UserAvatar name={assignedToName} size={size === 'sm' ? 'xs' : 'sm'} />
      <span className={cn(
        'font-medium text-text-secondary',
        size === 'sm' ? 'text-xs' : 'text-sm'
      )}>
        {assignedToName}
      </span>
    </div>
  );
}

// ============================================================================
// USER AVATAR
// ============================================================================

interface UserAvatarProps {
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

export function UserAvatar({ name, size = 'sm', className }: UserAvatarProps) {
  const sizeClasses = {
    xs: 'w-5 h-5 text-[10px]',
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
  };

  // Generate consistent color from name
  const colors = [
    'from-blue-500 to-purple-600',
    'from-green-500 to-teal-600',
    'from-orange-500 to-red-600',
    'from-pink-500 to-rose-600',
    'from-indigo-500 to-blue-600',
    'from-cyan-500 to-blue-600',
  ];
  const colorIndex = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;

  return (
    <div className={cn(
      'rounded-full bg-gradient-to-br flex items-center justify-center text-white font-medium',
      colors[colorIndex],
      sizeClasses[size],
      className
    )}>
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

// ============================================================================
// ICONS
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function UnassignedIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );
}
